import { SUBSCRIPTION_PLAN } from './school';
import { SCHOOL_SUBSCRIPTION_TYPE } from '../types/school';
import { SidebarItemExpandable, SimpleSidebarItem } from '../components/Sidebar/Sidebar';
import { DEFAULT_CLUB_TABLE_FILTERS } from '../views/GenericView/AdminView/Clubs/Clubs/Clubs';

export const ALL_SUBSCRIPTIONS_PLAN: SCHOOL_SUBSCRIPTION_TYPE[] = [
  SUBSCRIPTION_PLAN.LITE,
  SUBSCRIPTION_PLAN.FULL,
  SUBSCRIPTION_PLAN.TOURNAMENTS_PARTICIPATION,
  SUBSCRIPTION_PLAN.FREE_COMMUNICATION
];

const FREE_AND_FULL_SUBSCRIPTION_PLAN: SCHOOL_SUBSCRIPTION_TYPE[] = [
  SUBSCRIPTION_PLAN.FULL,
  SUBSCRIPTION_PLAN.FREE_COMMUNICATION
];

export function getSidebarItemsSlt(search = ''): SimpleSidebarItem[] {
  return [
    ['Overview','overview','home'],['Clubs','clubs','clubs'],['Sport','sport','teams'],['Students','students','children'],
    ['Reports','reports','consent'],['Thresholds & alerts','settings/thresholds','availability'],['My access','access','absence']
  ].map(([itemTitle,path,icon]) => {
    const params = new URLSearchParams(search);
    ['segment','page','clubId','sportId','snapshotId'].forEach(key => params.delete(key));
    if (path === 'clubs' || path === 'sport') params.set('area',path);
    if (path === 'overview') params.delete('area');
    return {itemTitle,to:{pathname:`/slt/${path}`,search:params.toString()?'?'+params.toString():''},visibleToSchoolSubscriptions:ALL_SUBSCRIPTIONS_PLAN,alwaysVisible:true,icon} as SimpleSidebarItem;
  });
}

export function getSidebarItemsAdmin(countBadges: {
  requests: string;
  bookings: string;
  inboxMessages: string;
  invites: string;
  tournamentInvites: string;
  waitingList: string;
  upcomingTournaments: string;
  tournamentConfirmationRequests: string;
}): (SidebarItemExpandable | SimpleSidebarItem)[] {
  return [
    {
      // Dashboard
      itemTitle: 'Dashboard',
      to: { pathname: '/dashboard' },
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
    },
    {
      itemTitle: 'Modules & plans',
      to: { pathname: '/school/modules' },
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
      isNew: false
    },
    {
      // Calendar
      itemTitle: 'Calendar',
      to: { pathname: '/calendar' },
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
    },
    {
      // Users
      title: 'People',
      items: [
        {
          to: { pathname: '/users' },
          itemTitle: 'All users and contacts',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/staff' },
          itemTitle: 'Staff',
          visibleToSchoolSubscriptions: [SUBSCRIPTION_PLAN.FULL]
        },
        {
          to: { pathname: '/students' },
          itemTitle: 'Students',
          visibleToSchoolSubscriptions: [SUBSCRIPTION_PLAN.FULL]
        },
        {
          to: { pathname: '/governors' },
          itemTitle: 'Trustees/Governors',
          visibleToSchoolSubscriptions: [SUBSCRIPTION_PLAN.FULL]
        },
        {
          to: { pathname: '/tags' },
          itemTitle: 'User tags',
          visibleToSchoolSubscriptions: FREE_AND_FULL_SUBSCRIPTION_PLAN
        },
        {
          to: { pathname: '/requests' },
          itemTitle: 'Role requests',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
          notificationCount: countBadges.requests
        }
      ]
    },

    {
      // Communications
      title: 'Communications',
      items: [
        {
          to: { pathname: '/generalMessages' },
          itemTitle: 'General Messages',
          visibleToSchoolSubscriptions: FREE_AND_FULL_SUBSCRIPTION_PLAN
        },
        {
          to: { pathname: '/eventCommunications' },
          itemTitle: 'Chats',
          visibleToSchoolSubscriptions: FREE_AND_FULL_SUBSCRIPTION_PLAN,
          notificationCount: (countBadges as any).eventCommunications
        },
        {
          to: { pathname: '/news' },
          itemTitle: 'News',
          visibleToSchoolSubscriptions: FREE_AND_FULL_SUBSCRIPTION_PLAN
        }
      ]
    },

    {
      // Admin console
      title: 'Admin console',
      items: [
        {
          to: { pathname: '/school/settings/base' },
          itemTitle: 'School settings',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/school/structure' },
          itemTitle: 'School structure',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/school/terms' },
          itemTitle: 'Term dates',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/awardTypes' },
          itemTitle: 'Award types',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/template/forms' },
          itemTitle: 'Form builder',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/activities' },
          itemTitle: 'Activities & Sports',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/schoolGallery' },
          itemTitle: 'School gallery',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/notificationSettings' },
          itemTitle: 'Notification settings',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/integrations' },
          itemTitle: 'Integrations',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    },
    {
      // Sport & Fixtures
      title: 'Sport & Fixtures',
      items: [
        {
          to: { pathname: '/teams' },
          itemTitle: 'Teams',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/events' },
          itemTitle: 'All events',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/records' },
          itemTitle: 'Records',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/venues' },
          itemTitle: 'Venues',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/invites/inbox' },
          itemTitle: 'Invites',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
          notificationCount: countBadges.invites
        },
        {
          to: { pathname: '/leagues' },
          itemTitle: 'School Leagues',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/kit-lab' },
          itemTitle: 'Kit Lab',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    },
    {
      // Clubs
      title: 'Clubs',
      items: [
        {
          to: { pathname: '/clubProgrammes' },
          itemTitle: 'Activity programmes',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/clubs', search: DEFAULT_CLUB_TABLE_FILTERS },
          itemTitle: 'Clubs',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/invitations' },
          itemTitle: 'Invitations',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    },
    {
      // Messages
      title: 'Messages',
      items: [
        {
          to: { pathname: '/messages/inbox' },
          itemTitle: 'Inbox',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
          notificationCount: countBadges.inboxMessages
        },
        {
          to: { pathname: '/messages/outbox' },
          itemTitle: 'Outbox',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/messages/archive' },
          itemTitle: 'Archive',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    },
    {
      // Awards
      title: 'Awards',
      items: [
        {
          to: { pathname: '/awards' },
          itemTitle: 'Awards',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/houseRace' },
          itemTitle: 'House Race',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    },
    {
      title: 'Tournaments',
      items: [
        {
          to: { pathname: '/tournaments/upcoming' },
          itemTitle: 'Upcoming',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
          notificationCount: countBadges.upcomingTournaments
        },
        {
          to: { pathname: '/tournaments/waitingList' },
          itemTitle: 'Waiting list',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
          notificationCount: countBadges.waitingList
        },
        {
          to: { pathname: '/tournaments/invites' },
          itemTitle: 'Invites',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
          notificationCount: countBadges.tournamentInvites
        },
        {
          to: { pathname: '/tournaments/requests' },
          itemTitle: 'Invites',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
          notificationCount: countBadges.tournamentConfirmationRequests
        },
        {
          to: { pathname: '/tournaments/archive' },
          itemTitle: 'Archive',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: {pathname: '/tournamentAvailability'},
          itemTitle: 'Union representation',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    }
  ];
}

export function getSidebarItemsManager(countBadges: {
  requests: string;
  inboxMessages: string;
  invites: string;
}): (SidebarItemExpandable | SimpleSidebarItem)[] {
  return [
    // {
    //   // Dashboard
    //   itemTitle: 'Dashboard',
    //   to: { pathname: '/dashboard' },
    //   visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
    // },
    {
      // Calendar
      itemTitle: 'Calendar',
      to: { pathname: '/calendar' },
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
    },
    {
      // Users
      title: 'People',
      items: [
        {
          to: { pathname: '/users' },
          itemTitle: 'All users and contacts',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/staff' },
          itemTitle: 'Staff',
          visibleToSchoolSubscriptions: [SUBSCRIPTION_PLAN.FULL]
        },
        {
          to: { pathname: '/students' },
          itemTitle: 'Students',
          visibleToSchoolSubscriptions: [SUBSCRIPTION_PLAN.FULL]
        },
        {
          to: { pathname: '/governors' },
          itemTitle: 'Trustees/Governors',
          visibleToSchoolSubscriptions: [SUBSCRIPTION_PLAN.FULL]
        },
        {
          to: { pathname: '/tags' },
          itemTitle: 'User tags',
          visibleToSchoolSubscriptions: FREE_AND_FULL_SUBSCRIPTION_PLAN
        },
        {
          to: { pathname: '/requests' },
          itemTitle: 'Role requests',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
          notificationCount: countBadges.requests
        }
      ]
    },
    {
      // Communications
      title: 'Communications',
      items: [
        {
          to: { pathname: '/generalMessages' },
          itemTitle: 'General Messages',
          visibleToSchoolSubscriptions: FREE_AND_FULL_SUBSCRIPTION_PLAN
        },
        {
          to: { pathname: '/eventCommunications' },
          itemTitle: 'Chats',
          visibleToSchoolSubscriptions: FREE_AND_FULL_SUBSCRIPTION_PLAN,
          notificationCount: (countBadges as any).eventCommunications
        },
        {
          to: { pathname: '/news' },
          itemTitle: 'News',
          visibleToSchoolSubscriptions: FREE_AND_FULL_SUBSCRIPTION_PLAN
        }
      ]
    },
    {
      // Admin console
      title: 'Admin console',
      items: [
        {
          to: { pathname: '/school/structure' },
          itemTitle: 'School structure',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    },
    {
      // Sport & Fixtures
      title: 'Sport & Fixtures',
      items: [
        {
          to: { pathname: '/teams' },
          itemTitle: 'Teams',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/events' },
          itemTitle: 'All events',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/venues' },
          itemTitle: 'Venues',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/invites/inbox' },
          itemTitle: 'Invites',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
          notificationCount: countBadges.invites
        },
        {
          to: { pathname: '/leagues' },
          itemTitle: 'School Leagues',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    },
    {
      // Clubs
      title: 'Clubs',
      items: [
        {
          to: { pathname: '/clubs', search: DEFAULT_CLUB_TABLE_FILTERS },
          itemTitle: 'Clubs',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/invitations' },
          itemTitle: 'Invitations',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    },
    {
      // Messages
      title: 'Messages',
      items: [
        {
          to: { pathname: '/messages/inbox' },
          itemTitle: 'Inbox',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
          notificationCount: countBadges.inboxMessages
        },
        {
          to: { pathname: '/messages/outbox' },
          itemTitle: 'Outbox',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/messages/archive' },
          itemTitle: 'Archive',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    }
  ];
}

export function getSidebarItemsParent(countBadges: {
  requests: string;
  bookings: string;
  inboxMessages: string;
  invites: string;
  availabilityReports: string;
  absenceRequests: string;
}) {
  return [
    {
      itemTitle: 'Home',
      to: { pathname: '/home' },
      icon: 'home' as const,
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
    },
    {
      itemTitle: 'Calendar',
      to: { pathname: '/calendar' },
      icon: 'calendar' as const,
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
    },
    {
      itemTitle: 'Children',
      to: { pathname: '/children' },
      icon: 'children' as const,
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
    },
    {
      itemTitle: 'Children’s teams',
      to: { pathname: '/childrenTeams' },
      icon: 'teams' as const,
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
    },
    {
      itemTitle: 'Awards & House Points',
      to: { pathname: '/awards/current' },
      icon: 'awards' as const,
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
    },
    {
      itemTitle: 'Consent requests',
      to: { pathname: '/consentRequests/inbox' },
      icon: 'consent' as const,
      activePathPrefixes: ['/consentRequests'],
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
      notificationCount: countBadges.requests
    },
    {
      itemTitle: 'Clubs',
      to: { pathname: '/clubs' },
      icon: 'clubs' as const,
      activePathPrefixes: ['/clubs', '/clubProgrammes', '/clubBookings'],
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
      notificationCount: countBadges.bookings
    },
    {
      itemTitle: 'Availability reports',
      to: { pathname: '/availabilityReports/outbox' },
      icon: 'availability' as const,
      activePathPrefixes: ['/availabilityReports'],
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
      notificationCount: countBadges.availabilityReports
    },
    {
      itemTitle: 'Absence requests',
      to: { pathname: '/absenceRequests/outbox' },
      icon: 'absence' as const,
      activePathPrefixes: ['/absenceRequests'],
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
      notificationCount: countBadges.absenceRequests
    }
  ];
}

export function getSidebarItemsStudent(countBadges: {
  requests: string;
  inboxMessages: string;
  invites: string;
  bookings: string;
  availabilityReports: string;
}) {
  return [
    {
      itemTitle: 'Today',
      to: { pathname: '/home' },
      icon: 'home' as const,
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
    },
    {
      itemTitle: 'Calendar',
      to: { pathname: '/calendar' },
      icon: 'calendar' as const,
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
    },
    {
      itemTitle: 'My teams',
      to: { pathname: '/myTeams' },
      icon: 'teams' as const,
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
    },
    {
      itemTitle: 'Clubs',
      to: { pathname: '/clubs' },
      icon: 'clubs' as const,
      activePathPrefixes: ['/clubs', '/clubProgrammes', '/clubBookings'],
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
      notificationCount: countBadges.bookings
    },
    {
      itemTitle: 'Availability reports',
      to: { pathname: '/availabilityReports/outbox' },
      icon: 'availability' as const,
      activePathPrefixes: ['/availabilityReports'],
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
      notificationCount: countBadges.availabilityReports
    },
    {
      itemTitle: 'Awards & House Points',
      to: { pathname: '/awards/current' },
      icon: 'awards' as const,
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
    }
  ];
}

export function getSidebarItemsCoach(countBadges: { requests: string; inboxMessages: string; invites: string }) {
  return [
    // {
    //   // Dashboard
    //   itemTitle: 'Dashboard',
    //   to: { pathname: '/dashboard' },
    //   visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
    // },
    {
      // Calendar
      itemTitle: 'Calendar',
      to: { pathname: '/calendar' },
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
      alwaysVisible: true
    },
    {
      // Sport & Fixtures
      title: 'Sport & Fixtures',
      items: [
        {
          to: { pathname: '/teams' },
          itemTitle: 'Teams',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/events' },
          itemTitle: 'All events',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/invites/inbox' },
          itemTitle: 'Invites',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
          notificationCount: countBadges.invites
        },
        {
          to: { pathname: '/leagues' },
          itemTitle: 'School Leagues',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    },
    {
      // Clubs
      title: 'Clubs',
      items: [
        {
          to: { pathname: '/clubs', search: DEFAULT_CLUB_TABLE_FILTERS },
          itemTitle: 'Clubs',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/invitations' },
          itemTitle: 'Invitations',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    },
    {
      // Messages
      title: 'Messages',
      items: [
        {
          to: { pathname: '/messages/inbox' },
          itemTitle: 'Inbox',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
          notificationCount: countBadges.inboxMessages
        },
        {
          to: { pathname: '/messages/outbox' },
          itemTitle: 'Outbox',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/messages/archive' },
          itemTitle: 'Archive',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    }
  ];
}

export function getSidebarItemsTeacher(countBadges: { requests: string; inboxMessages: string; invites: string }) {
  return [
    // {
    //   // Dashboard
    //   itemTitle: 'Dashboard',
    //   to: { pathname: '/dashboard' },
    //   visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
    // },
    {
      // Calendar
      itemTitle: 'Calendar',
      to: { pathname: '/calendar' },
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
      alwaysVisible: true
    },
    {
      // Sport & Fixtures
      title: 'Sport & Fixtures',
      items: [
        {
          to: { pathname: '/teams' },
          itemTitle: 'Teams',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/events' },
          itemTitle: 'All events',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/invites/inbox' },
          itemTitle: 'Invites',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
          notificationCount: countBadges.invites
        },
        {
          to: { pathname: '/leagues' },
          itemTitle: 'School Leagues',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    },
    {
      // Clubs
      title: 'Clubs',
      items: [
        {
          to: { pathname: '/clubs', search: DEFAULT_CLUB_TABLE_FILTERS },
          itemTitle: 'Clubs',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/invitations' },
          itemTitle: 'Invitations',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    },
    {
      // Messages
      title: 'Messages',
      items: [
        {
          to: { pathname: '/messages/inbox' },
          itemTitle: 'Inbox',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
          notificationCount: countBadges.inboxMessages
        },
        {
          to: { pathname: '/messages/outbox' },
          itemTitle: 'Outbox',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/messages/archive' },
          itemTitle: 'Archive',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    }
  ];
}

export function getSidebarItemsAdminSU(countBadges: {
  requests: string;
  bookings: string;
  inboxMessages: string;
  invites: string;
  waitingList: string;
  upcomingTournaments: string;
}): (SidebarItemExpandable | SimpleSidebarItem)[] {
  return [
    {
      to: { pathname: '/dashboard' },
      itemTitle: 'Dashboard',
      visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
      alwaysVisible: true
    },
    {
      // Users
      title: 'People',
      items: [
        {
          to: { pathname: '/staff' },
          itemTitle: 'Union Admins',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/requests' },
          itemTitle: 'Role requests',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN,
          notificationCount: countBadges.requests
        }
      ]
    },
    {
      // Championships
      title: 'Championships',
      items: [
        {
          to: { pathname: '/championships' },
          itemTitle: 'Championships',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    },
    {
      // Tournaments
      title: 'Tournaments',
      items: [
        {
          to: { pathname: '/tournaments/upcoming' },
          itemTitle: 'Upcoming tournaments',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/tournaments/archive' },
          itemTitle: 'Tournaments archive',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/tournaments/invites/outbox' },
          itemTitle: 'Invites',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    },
    {
      // Tournaments
      title: 'High-level tournaments',
      items: [
        {
          to: { pathname: '/highLevelTournaments/upcoming' },
          itemTitle: 'Upcoming high-level tournaments',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/highLevelTournaments/archive' },
          itemTitle: 'High-level tournaments archive',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    },
    {
      // Schools
      title: 'Schools',
      items: [
        {
          to: { pathname: '/schools' },
          itemTitle: 'Schools',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/schoolsAdmins' },
          itemTitle: 'School admins',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    },
    {
      // Subscriptions
      title: 'Admin console',
      items: [
        {
          to: { pathname: '/activities' },
          itemTitle: 'Activities & Sports',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/venues' },
          itemTitle: 'Venues',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/notificationSettings' },
          itemTitle: 'Notification settings',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/template/tournamentRequests' },
          itemTitle: 'Form builder',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/tournamentTags' },
          itemTitle: 'Tournament tags',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/records' },
          itemTitle: 'Records',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/documents' },
          itemTitle: 'Downloadable documents',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/unionGallery' },
          itemTitle: 'Union gallery',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/news' },
          itemTitle: 'News',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        },
        {
          to: { pathname: '/union/settings/general' },
          itemTitle: 'Union settings',
          visibleToSchoolSubscriptions: ALL_SUBSCRIPTIONS_PLAN
        }
      ]
    }
  ];
}
